import React, { useState, useEffect, useCallback } from 'react';
import './Products.css';
import './AllSellStyles.css';
import './CommonStyles.css';
import { exportToCSV, exportToExcel, exportToPDF, printPage } from './exportUtils';
import {
    getProductSellReport,
    getProductSellReportWithPurchase,
    getProductSellGroupedByDate,
    getProductSellByCategory,
    getProductSellByBrand,
    getProductSellFilterOptions
} from '../../services/reportApi';
import { formatDateTime, formatDate } from '../../utils/dateUtils';

const ProductSellReport = () => {
    const [entriesPerPage, setEntriesPerPage] = useState(25);
    const [searchTerm, setSearchTerm] = useState('');
    const [showExportDropdown, setShowExportDropdown] = useState(false);
    const [activeSubTab, setActiveSubTab] = useState('detailed');
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [lastPage, setLastPage] = useState(1);
    const [totalSubtotal, setTotalSubtotal] = useState(0);
    const [totalSubtotalFormatted, setTotalSubtotalFormatted] = useState('');
    const [totalTaxFormatted, setTotalTaxFormatted] = useState('');
    const [totalQtySold, setTotalQtySold] = useState(0);
    const [totalStock, setTotalStock] = useState(0);
    const [filterOptions, setFilterOptions] = useState({ locations: [], customers: [], categories: [], brands: [], customer_groups: [] });
    const [filters, setFilters] = useState({ start_date: '', end_date: '', location_id: '', customer_id: '', category_id: '', brand_id: '', customer_group_id: '' });
    const [pendingFilters, setPendingFilters] = useState({ ...filters });
    const [showFilters, setShowFilters] = useState(false);

    const fetchData = useCallback(async (pg = 1, perPage = entriesPerPage) => {
        setLoading(true); setError(null);
        try {
            let apiFn = getProductSellReport;
            if (activeSubTab === 'detailed-with-purchase') apiFn = getProductSellReportWithPurchase;
            else if (activeSubTab === 'grouped-by-date') apiFn = getProductSellGroupedByDate;
            else if (activeSubTab === 'by-category') apiFn = getProductSellByCategory;
            else if (activeSubTab === 'by-brand') apiFn = getProductSellByBrand;

            const res = await apiFn({ ...filters, page: pg, per_page: perPage });
            if (res.success) {
                setData(res.data.data || []);
                setTotal(res.data.total || 0);
                setLastPage(res.data.last_page || 1);
                setPage(res.data.current_page || pg);
                setTotalSubtotalFormatted(res.data.total_subtotal_formatted || '');
                setTotalSubtotal(res.data.total_subtotal || 0);
                setTotalTaxFormatted(res.data.total_tax_formatted || '');
                setTotalQtySold(res.data.total_qty_sold || 0);
                setTotalStock(res.data.total_stock || 0);
            }
        } catch (e) { setError(e.message); } finally { setLoading(false); }
    }, [filters, entriesPerPage, activeSubTab]);

    useEffect(() => {
        getProductSellFilterOptions().then(r => { if (r.success) setFilterOptions(r.data); }).catch(() => {});
    }, []);

    useEffect(() => {
        fetchData(1, entriesPerPage);
    }, [filters, entriesPerPage, activeSubTab, fetchData]);

    const handleOpenFilters = () => {
        setPendingFilters({ ...filters });
        setShowFilters(true);
    };

    const handleFilterApply = () => {
        setFilters({ ...pendingFilters });
        setShowFilters(false);
    };

    const handleFilterReset = () => {
        const emptyFilters = { start_date: '', end_date: '', location_id: '', customer_id: '', category_id: '', brand_id: '', customer_group_id: '' };
        setPendingFilters(emptyFilters);
        setFilters(emptyFilters);
        setShowFilters(false);
    };

    const filteredData = data.filter(r => {
        const s = searchTerm.toLowerCase();
        return (r.product_name || '').toLowerCase().includes(s) ||
            (r.sub_sku || '').toLowerCase().includes(s) ||
            (r.customer || '').toLowerCase().includes(s) ||
            (r.category_name || '').toLowerCase().includes(s) ||
            (r.brand_name || '').toLowerCase().includes(s);
    });

    const qtyByUnit = filteredData.reduce((acc, r) => {
        const unit = r.unit || 'Pc(s)';
        const qty = parseFloat(r.sell_qty || r.purchase_quantity || r.total_qty_sold) || 0;
        acc[unit] = (acc[unit] || 0) + qty;
        return acc;
    }, {});

    const qtySummaryLines = Object.entries(qtyByUnit).map(([unit, qty]) => `${qty.toFixed(2)} ${unit}`);

    const getHeaders = () => {
        if (activeSubTab === 'detailed-with-purchase') {
            return ['Product', 'SKU', 'Customer name', 'Invoice No.', 'Date', 'Purchase ref no.', 'Supplier Name', 'Quantity'];
        }
        if (activeSubTab === 'grouped-by-date') {
            return ['Product', 'SKU', 'Date', 'Current stock', 'Total unit sold', 'Total'];
        }
        if (activeSubTab === 'by-category') {
            return ['Category', 'Current stock', 'Total unit sold', 'Total'];
        }
        if (activeSubTab === 'by-brand') {
            return ['Brand', 'Current stock', 'Total unit sold', 'Total'];
        }
        return ['PRODUCT', 'SKU', 'CUSTOMER', 'CONTACT ID', 'INVOICE NO.', 'DATE', 'QUANTITY', 'UNIT PRICE', 'DISCOUNT', 'TAX', 'PRICE INC. TAX', 'TOTAL'];
    };

    const tableHeaders = getHeaders();

    const handleExport = (format) => {
        const headers = tableHeaders;
        let rows = [];
        if (activeSubTab === 'detailed-with-purchase') {
            rows = filteredData.map(r => [
                r.product_name || '', r.sub_sku || '', r.customer || '', r.invoice_no || '',
                r.transaction_date ? formatDateTime(r.transaction_date) : '',
                r.purchase_ref_no || '', r.supplier_name || '', `${r.purchase_quantity} ${r.unit || ''}`.trim()
            ]);
        } else if (activeSubTab === 'grouped-by-date') {
            rows = filteredData.map(r => [
                r.product_name || '', r.sub_sku || '', r.transaction_date ? formatDate(r.transaction_date) : '',
                r.current_stock !== null ? `${r.current_stock} ${r.unit || ''}` : '--',
                `${r.total_qty_sold} ${r.unit || ''}`, r.subtotal_formatted || ''
            ]);
        } else if (activeSubTab === 'by-category') {
            rows = filteredData.map(r => [
                r.category_name || '', r.current_stock || 0, r.total_qty_sold || 0, r.subtotal_formatted || ''
            ]);
        } else if (activeSubTab === 'by-brand') {
            rows = filteredData.map(r => [
                r.brand_name || '', r.current_stock || 0, r.total_qty_sold || 0, r.subtotal_formatted || ''
            ]);
        } else {
            rows = filteredData.map(r => [
                r.product_name || '', r.sub_sku || '', r.customer || '', r.contact_id || '',
                r.invoice_no || '', r.transaction_date ? formatDateTime(r.transaction_date) : '',
                `${r.sell_qty ?? ''} ${r.unit || ''}`.trim(),
                r.unit_price_formatted || '', r.discount_amount || '', r.item_tax || '',
                r.unit_sale_price_formatted || '', r.subtotal_formatted || ''
            ]);
        }

        const title = `Product Sell Report (${activeSubTab})`; const filename = `product_sell_report_${activeSubTab}`;
        if (format === 'CSV') exportToCSV(headers, rows, filename);
        else if (format === 'Excel') exportToExcel(headers, rows, filename);
        else if (format === 'PDF') exportToPDF(title, headers, rows, filename);
        else if (format === 'Print') printPage();
        setShowExportDropdown(false);
    };

    const from = total === 0 ? 0 : (page - 1) * entriesPerPage + 1;
    const to = Math.min(page * entriesPerPage, total);

    const subTabs = [
        { id: 'detailed', label: 'Detailed', icon: 'fa-list-ul' },
        { id: 'detailed-with-purchase', label: 'Detailed (With purchase)', icon: 'fa-list-ul' },
        { id: 'grouped-by-date', label: 'Grouped (By Date)', icon: 'fa-align-justify' },
        { id: 'by-category', label: 'By Category', icon: 'fa-align-justify' },
        { id: 'by-brand', label: 'By Brand', icon: 'fa-align-justify' },
    ];

    return (
        <div style={{ paddingTop: '0.5rem' }}>

            {/* ── Sub-tabs ── */}
            <div style={{ display: 'flex', gap: '0', borderBottom: '1px solid var(--etpos-border, #e2e8f0)', marginBottom: '1rem', flexWrap: 'wrap' }}>
                {subTabs.map(tab => (
                    <button
                        key={tab.id}
                        onClick={() => {
                            setActiveSubTab(tab.id);
                            setPage(1);
                        }}
                        style={{
                            padding: '0.75rem 1rem',
                            background: 'none',
                            border: 'none',
                            borderBottom: activeSubTab === tab.id ? '2px solid #3b82f6' : '2px solid transparent',
                            color: activeSubTab === tab.id ? '#3b82f6' : 'var(--etpos-text-muted, #64748b)',
                            fontWeight: activeSubTab === tab.id ? '600' : '500',
                            fontSize: '0.875rem',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem'
                        }}
                    >
                        <i className={`fa ${tab.icon}`} />
                        {tab.label}
                    </button>
                ))}
            </div>

            {/* ── Product Sell Table Container ── */}
            <div style={{ background: 'var(--etpos-card, #fff)', border: '1px solid var(--etpos-border, #e2e8f0)', borderRadius: '10px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>

                {/* Table controls */}
                <div className="table-controls" style={{ borderBottom: '1px solid var(--etpos-border, #e2e8f0)', padding: '1rem 1.25rem', position: 'relative', zIndex: 20 }}>
                    <div className="table-controls-left">
                        <div className="entries-select">
                            <span>Show</span>
                            <select value={entriesPerPage} onChange={e => setEntriesPerPage(Number(e.target.value))}>
                                <option value={25}>25</option>
                                <option value={50}>50</option>
                                <option value={100}>100</option>
                            </select>
                            <span>entries</span>
                        </div>
                    </div>

                    <div className="table-controls-right" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <button
                            onClick={handleOpenFilters}
                            style={{
                                padding: '0.4rem 0.8rem',
                                border: '1px solid var(--etpos-border, #e2e8f0)',
                                borderRadius: '6px',
                                background: 'var(--etpos-card, #fff)',
                                cursor: 'pointer',
                                fontSize: '0.85rem',
                                color: 'var(--etpos-text, #334155)',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.4rem',
                                fontWeight: '500'
                            }}
                        >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z" />
                            </svg>
                            Filters
                        </button>

                        <div className="export-dropdown-container" style={{ position: 'relative', zIndex: 100 }}>
                            <button className="export-btn" onClick={() => setShowExportDropdown(!showExportDropdown)} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.4rem 0.8rem', border: '1px solid var(--etpos-border, #e2e8f0)', borderRadius: '6px', background: 'var(--etpos-card, #fff)', cursor: 'pointer', fontSize: '0.85rem', color: 'var(--etpos-text, #334155)' }}>
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                                Export ▾
                            </button>
                            {showExportDropdown && (
                                <div className="export-dropdown-menu" style={{ position: 'absolute', top: '100%', right: 0, marginTop: '0.25rem', background: 'var(--etpos-card, #fff)', border: '1px solid var(--etpos-border, #e2e8f0)', borderRadius: '6px', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05)', zIndex: 9999, minWidth: '160px' }}>
                                    {[
                                        { label: 'Export CSV', action: () => handleExport('CSV'), icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg> },
                                        { label: 'Export Excel', action: () => handleExport('Excel'), icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><path d="M8 13h8"/><path d="M8 17h8"/></svg> },
                                        { label: 'Print', action: () => handleExport('Print'), icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#475569" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg> },
                                        { label: 'Export PDF', action: () => handleExport('PDF'), icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><path d="M9 15v-6h2a2 2 0 0 1 0 4H9"/></svg> }
                                    ].map((item, i) => (
                                        <button key={i} onClick={() => { if (item.action) item.action(); setShowExportDropdown(false); }} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', width: '100%', padding: '0.55rem 1rem', border: 'none', background: 'none', textAlign: 'left', cursor: 'pointer', fontSize: '0.8125rem', color: 'var(--etpos-text, #475569)' }} onMouseEnter={e => e.currentTarget.style.backgroundColor = 'var(--etpos-bg, #f1f5f9)'} onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}>
                                            {item.icon}
                                            {item.label}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                        <input type="text" placeholder="Search ..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} style={{ padding: '0.4rem 0.75rem', border: '1px solid var(--etpos-border, #e2e8f0)', borderRadius: '6px', fontSize: '0.8125rem', outline: 'none', width: '180px', background: 'var(--etpos-card, #fff)', color: 'var(--etpos-text, #334155)' }} />
                    </div>
                </div>

                {error && <div style={{ padding: '1rem', color: '#ef4444', fontSize: '0.875rem' }}>Error: {error}</div>}

                {/* Table */}
                <div style={{ overflowX: 'auto' }}>
                    <table className="products-table" style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                        <thead>
                            <tr style={{ background: 'var(--etpos-bg, #f8fafc)', borderBottom: '1px solid var(--etpos-border, #e2e8f0)' }}>
                                {tableHeaders.map((h, i) => (
                                    <th key={h} style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem', color: 'var(--etpos-text, #1e293b)', fontWeight: '600', whiteSpace: 'nowrap', minWidth: (i === 0 && activeSubTab.includes('detailed')) ? '220px' : 'auto' }}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan={tableHeaders.length} style={{ padding: '2rem', textAlign: 'center', color: 'var(--etpos-text-muted, #64748b)' }}>Loading...</td></tr>
                            ) : filteredData.length === 0 ? (
                                <tr><td colSpan={tableHeaders.length} style={{ padding: '2rem', textAlign: 'center', color: 'var(--etpos-text-muted, #64748b)' }}>No product sell records found</td></tr>
                            ) : filteredData.map((row, idx) => (
                                <tr key={idx} style={{ borderBottom: '1px solid var(--etpos-border, #f1f5f9)' }}>
                                    {/* 1. DETAILED TAB */}
                                    {activeSubTab === 'detailed' && (
                                        <>
                                            <td style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem', fontWeight: '600', color: 'var(--etpos-text, #1e293b)', minWidth: '220px' }}>{row.product_name}</td>
                                            <td style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem', color: 'var(--etpos-text, #475569)' }}>
                                                <span style={{ background: 'var(--etpos-bg, #f1f5f9)', padding: '0.15rem 0.4rem', borderRadius: '4px', fontFamily: 'monospace' }}>{row.sub_sku}</span>
                                            </td>
                                            <td style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem', color: 'var(--etpos-text, #475569)', minWidth: '120px' }}>{row.customer}</td>
                                            <td style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem', color: 'var(--etpos-text, #475569)' }}>{row.contact_id}</td>
                                            <td style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem', color: '#2563eb', fontWeight: '600' }}>{row.invoice_no}</td>
                                            <td style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem', color: 'var(--etpos-text, #475569)', whiteSpace: 'nowrap' }}>{row.transaction_date ? formatDateTime(row.transaction_date) : ''}</td>
                                            <td style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem', fontWeight: '600', color: 'var(--etpos-text, #1e293b)' }}>{row.sell_qty} {row.unit}</td>
                                            <td style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem', color: 'var(--etpos-text, #475569)' }}>{row.unit_price_formatted}</td>
                                            <td style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem', color: 'var(--etpos-text, #475569)' }}>{row.discount_type === 'percentage' ? `${row.discount_amount}%` : row.discount_amount}</td>
                                            <td style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem', color: 'var(--etpos-text, #475569)' }}>{row.item_tax}</td>
                                            <td style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem', color: 'var(--etpos-text, #475569)' }}>{row.unit_sale_price_formatted}</td>
                                            <td style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem', fontWeight: '700', color: '#10b981' }}>{row.subtotal_formatted}</td>
                                        </>
                                    )}

                                    {/* 2. DETAILED (WITH PURCHASE) TAB */}
                                    {activeSubTab === 'detailed-with-purchase' && (
                                        <>
                                            <td style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem', fontWeight: '600', color: 'var(--etpos-text, #1e293b)', minWidth: '220px' }}>{row.product_name}</td>
                                            <td style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem', color: 'var(--etpos-text, #475569)' }}>
                                                <span style={{ background: 'var(--etpos-bg, #f1f5f9)', padding: '0.15rem 0.4rem', borderRadius: '4px', fontFamily: 'monospace' }}>{row.sub_sku}</span>
                                            </td>
                                            <td style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem', color: 'var(--etpos-text, #475569)', minWidth: '120px' }}>{row.customer}</td>
                                            <td style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem', color: '#2563eb', fontWeight: '600' }}>{row.invoice_no}</td>
                                            <td style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem', color: 'var(--etpos-text, #475569)', whiteSpace: 'nowrap' }}>{row.transaction_date ? formatDateTime(row.transaction_date) : ''}</td>
                                            <td style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem', color: 'var(--etpos-text, #475569)' }}>{row.purchase_ref_no}</td>
                                            <td style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem', color: 'var(--etpos-text, #475569)' }}>{row.supplier_name}</td>
                                            <td style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem', fontWeight: '600', color: 'var(--etpos-text, #1e293b)' }}>{row.purchase_quantity} {row.unit}</td>
                                        </>
                                    )}

                                    {/* 3. GROUPED (BY DATE) TAB */}
                                    {activeSubTab === 'grouped-by-date' && (
                                        <>
                                            <td style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem', fontWeight: '600', color: 'var(--etpos-text, #1e293b)' }}>{row.product_name}</td>
                                            <td style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem', color: 'var(--etpos-text, #475569)' }}>
                                                <span style={{ background: 'var(--etpos-bg, #f1f5f9)', padding: '0.15rem 0.4rem', borderRadius: '4px', fontFamily: 'monospace' }}>{row.sub_sku}</span>
                                            </td>
                                            <td style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem', color: 'var(--etpos-text, #475569)', whiteSpace: 'nowrap' }}>{row.transaction_date ? formatDate(row.transaction_date) : ''}</td>
                                            <td style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem', color: 'var(--etpos-text, #475569)' }}>{row.current_stock !== null ? `${row.current_stock} ${row.unit || ''}` : ''}</td>
                                            <td style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem', fontWeight: '600', color: 'var(--etpos-text, #1e293b)' }}>{row.total_qty_sold} {row.unit}</td>
                                            <td style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem', fontWeight: '700', color: '#10b981' }}>{row.subtotal_formatted}</td>
                                        </>
                                    )}

                                    {/* 4. BY CATEGORY TAB */}
                                    {activeSubTab === 'by-category' && (
                                        <>
                                            <td style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem', fontWeight: '600', color: 'var(--etpos-text, #1e293b)' }}>{row.category_name}</td>
                                            <td style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem', color: 'var(--etpos-text, #475569)' }}>{row.current_stock}</td>
                                            <td style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem', fontWeight: '600', color: 'var(--etpos-text, #1e293b)' }}>{row.total_qty_sold}</td>
                                            <td style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem', fontWeight: '700', color: '#10b981' }}>{row.subtotal_formatted}</td>
                                        </>
                                    )}

                                    {/* 5. BY BRAND TAB */}
                                    {activeSubTab === 'by-brand' && (
                                        <>
                                            <td style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem', fontWeight: '600', color: 'var(--etpos-text, #1e293b)' }}>{row.brand_name}</td>
                                            <td style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem', color: 'var(--etpos-text, #475569)' }}>{row.current_stock}</td>
                                            <td style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem', fontWeight: '600', color: 'var(--etpos-text, #1e293b)' }}>{row.total_qty_sold}</td>
                                            <td style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem', fontWeight: '700', color: '#10b981' }}>{row.subtotal_formatted}</td>
                                        </>
                                    )}
                                </tr>
                            ))}
                        </tbody>

                        {/* ── Table Footer ── */}
                        <tfoot>
                            <tr style={{ background: 'var(--etpos-bg, #f1f5f9)', borderTop: '2px solid var(--etpos-border, #cbd5e1)', fontWeight: '700' }}>
                                {activeSubTab === 'detailed' && (
                                    <>
                                        <td colSpan="6" style={{ padding: '0.75rem 1rem', textAlign: 'right', fontSize: '0.875rem', color: 'var(--etpos-text, #1e293b)' }}>
                                            Total:
                                        </td>
                                        <td style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem', color: 'var(--etpos-text, #1e293b)', fontWeight: '600', whiteSpace: 'nowrap' }}>
                                            {qtySummaryLines.length > 0 ? qtySummaryLines.join(', ') : '0.00'}
                                        </td>
                                        <td style={{ padding: '0.75rem 1rem' }}></td>
                                        <td style={{ padding: '0.75rem 1rem' }}></td>
                                        <td style={{ padding: '0.75rem 1rem', fontSize: '0.875rem', color: 'var(--etpos-text, #1e293b)', fontWeight: '700', whiteSpace: 'nowrap' }}>
                                            {totalTaxFormatted ? totalTaxFormatted : `$ ${(filteredData.reduce((sum, r) => sum + (parseFloat(r.item_tax) || 0), 0)).toFixed(2)}`}
                                        </td>
                                        <td style={{ padding: '0.75rem 1rem' }}></td>
                                        <td style={{ padding: '0.75rem 1rem', fontSize: '0.875rem', color: 'var(--etpos-text, #1e293b)', fontWeight: '700', whiteSpace: 'nowrap' }}>
                                            {totalSubtotalFormatted || `$ ${totalSubtotal.toFixed(2)}`}
                                        </td>
                                    </>
                                )}

                                {activeSubTab === 'grouped-by-date' && (
                                    <>
                                        <td colSpan="4" style={{ padding: '0.75rem 1rem', textAlign: 'right', fontSize: '0.875rem', color: 'var(--etpos-text, #1e293b)' }}>
                                            Total:
                                        </td>
                                        <td style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem', color: 'var(--etpos-text, #1e293b)', fontWeight: '600', whiteSpace: 'nowrap' }}>
                                            {qtySummaryLines.length > 0 ? qtySummaryLines.join(', ') : '0.00'}
                                        </td>
                                        <td style={{ padding: '0.75rem 1rem', fontSize: '0.875rem', color: 'var(--etpos-text, #1e293b)', fontWeight: '700', whiteSpace: 'nowrap' }}>
                                            {totalSubtotalFormatted || `$ ${totalSubtotal.toFixed(2)}`}
                                        </td>
                                    </>
                                )}

                                {(activeSubTab === 'by-category' || activeSubTab === 'by-brand') && (
                                    <>
                                        <td style={{ padding: '0.75rem 1rem', textAlign: 'right', fontSize: '0.875rem', color: 'var(--etpos-text, #1e293b)' }}>
                                            Total:
                                        </td>
                                        <td style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem', color: 'var(--etpos-text, #1e293b)', fontWeight: '600', whiteSpace: 'nowrap' }}>
                                            {totalStock}
                                        </td>
                                        <td style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem', color: 'var(--etpos-text, #1e293b)', fontWeight: '600', whiteSpace: 'nowrap' }}>
                                            {totalQtySold}
                                        </td>
                                        <td style={{ padding: '0.75rem 1rem', fontSize: '0.875rem', color: 'var(--etpos-text, #1e293b)', fontWeight: '700', whiteSpace: 'nowrap' }}>
                                            {totalSubtotalFormatted || `$ ${totalSubtotal.toFixed(2)}`}
                                        </td>
                                    </>
                                )}
                            </tr>
                        </tfoot>
                    </table>
                </div>

                {/* Pagination */}
                <div className="pagination" style={{ padding: '0.85rem 1.25rem', borderTop: '1px solid var(--etpos-border, #e2e8f0)' }}>
                    <div className="pagination-info" style={{ fontSize: '0.8125rem', color: 'var(--etpos-text-muted, #475569)' }}>
                        {total > 0 ? `Showing ${from} to ${to} of ${total} entries` : 'No entries'}
                    </div>
                    <div className="pagination-controls" style={{ display: 'flex', gap: '0.5rem' }}>
                        <button className="pagination-btn" disabled={page <= 1} onClick={() => { if (page > 1) fetchData(page - 1); }}>Previous</button>
                        <span style={{ display: 'flex', alignItems: 'center', padding: '0 0.5rem', fontSize: '0.875rem', color: 'var(--etpos-text-muted, #475569)' }}>Page {page} of {lastPage}</span>
                        <button className="pagination-btn" disabled={page >= lastPage} onClick={() => { if (page < lastPage) fetchData(page + 1); }}>Next</button>
                    </div>
                </div>

            </div>

            {/* ── Filters Popup Modal ── */}
            {showFilters && (
                <div className="modal-overlay" onClick={() => setShowFilters(false)}>
                    <div className="modal-container filter-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '650px', width: '95%' }}>
                        <div className="modal-header">
                            <h2 style={{ fontSize: '1.1rem', margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z" />
                                </svg>
                                Filter Product Sell Report
                            </h2>
                            <button className="modal-close-btn" onClick={() => setShowFilters(false)}>✕</button>
                        </div>
                        <div className="modal-content" style={{ padding: '1.5rem', maxWidth: 'none' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                                <div>
                                    <label style={{ display: 'block', fontSize: '0.8125rem', fontWeight: '600', color: '#475569', marginBottom: '0.35rem' }}>Start Date:</label>
                                    <input type="date" value={pendingFilters.start_date} onChange={e => setPendingFilters(f => ({ ...f, start_date: e.target.value }))} style={{ width: '100%', padding: '0.45rem 0.75rem', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '0.875rem', outline: 'none' }} />
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontSize: '0.8125rem', fontWeight: '600', color: '#475569', marginBottom: '0.35rem' }}>End Date:</label>
                                    <input type="date" value={pendingFilters.end_date} onChange={e => setPendingFilters(f => ({ ...f, end_date: e.target.value }))} style={{ width: '100%', padding: '0.45rem 0.75rem', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '0.875rem', outline: 'none' }} />
                                </div>
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                                <div>
                                    <label style={{ display: 'block', fontSize: '0.8125rem', fontWeight: '600', color: '#475569', marginBottom: '0.35rem' }}>Location:</label>
                                    <select value={pendingFilters.location_id} onChange={e => setPendingFilters(f => ({ ...f, location_id: e.target.value }))} style={{ width: '100%', padding: '0.45rem 0.75rem', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '0.875rem', outline: 'none' }}>
                                        <option value="">All Locations</option>
                                        {filterOptions.locations?.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontSize: '0.8125rem', fontWeight: '600', color: '#475569', marginBottom: '0.35rem' }}>Customer:</label>
                                    <select value={pendingFilters.customer_id} onChange={e => setPendingFilters(f => ({ ...f, customer_id: e.target.value }))} style={{ width: '100%', padding: '0.45rem 0.75rem', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '0.875rem', outline: 'none' }}>
                                        <option value="">All Customers</option>
                                        {filterOptions.customers?.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                                    </select>
                                </div>
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                                <div>
                                    <label style={{ display: 'block', fontSize: '0.8125rem', fontWeight: '600', color: '#475569', marginBottom: '0.35rem' }}>Category:</label>
                                    <select value={pendingFilters.category_id} onChange={e => setPendingFilters(f => ({ ...f, category_id: e.target.value }))} style={{ width: '100%', padding: '0.45rem 0.75rem', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '0.875rem', outline: 'none' }}>
                                        <option value="">All Categories</option>
                                        {filterOptions.categories?.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontSize: '0.8125rem', fontWeight: '600', color: '#475569', marginBottom: '0.35rem' }}>Customer Group:</label>
                                    <select value={pendingFilters.customer_group_id} onChange={e => setPendingFilters(f => ({ ...f, customer_group_id: e.target.value }))} style={{ width: '100%', padding: '0.45rem 0.75rem', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '0.875rem', outline: 'none' }}>
                                        <option value="">All Customer Groups</option>
                                        {filterOptions.customer_groups?.map(cg => <option key={cg.id} value={cg.id}>{cg.name}</option>)}
                                    </select>
                                </div>
                            </div>
                        </div>
                        <div style={{ padding: '1rem 1.5rem', background: 'var(--etpos-card, #fff)', borderTop: '1px solid var(--etpos-border, #e2e8f0)', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '0.75rem' }}>
                            <button
                                type="button"
                                onClick={handleFilterReset}
                                style={{
                                    height: '38px',
                                    padding: '0 1.25rem',
                                    border: '1px solid #cbd5e1',
                                    borderRadius: '6px',
                                    background: '#f8fafc',
                                    color: '#475569',
                                    fontSize: '0.875rem',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    transition: 'all 0.15s ease'
                                }}
                            >
                                Reset
                            </button>
                            <button
                                type="button"
                                onClick={handleFilterApply}
                                style={{
                                    height: '38px',
                                    padding: '0 1.25rem',
                                    border: 'none',
                                    borderRadius: '6px',
                                    background: '#3b82f6',
                                    color: '#ffffff',
                                    fontSize: '0.875rem',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    boxShadow: '0 1px 2px rgba(59,130,246,0.2)',
                                    transition: 'all 0.15s ease'
                                }}
                            >
                                Apply Filters
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProductSellReport;
